import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  AlertTriangle,
  Calendar,
  Users,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  TreePine,
  RefreshCw,
  Download,
  Upload
} from 'lucide-react';

// API Configuration
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminVacationOverview = ({ selectedYear, onYearChange }) => {
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  useEffect(() => {
    fetchVacationOverview();
  }, [selectedYear]);

  const fetchVacationOverview = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API}/admin/vacation-overview/${selectedYear}`);
      setOverviewData(response.data);
    } catch (error) {
      console.error('Error fetching vacation overview:', error);
      setError('Fehler beim Laden der Urlaubsübersicht');
    } finally {
      setLoading(false);
    }
  };

  const getUtilizationColor = (percentage) => {
    if (percentage > 100) return 'text-red-600 bg-red-50 border-red-200';
    if (percentage > 80) return 'text-orange-600 bg-orange-50 border-orange-200';
    if (percentage > 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-green-600 bg-green-50 border-green-200';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nicht verfügbar';
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API}/vacations/export/excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `urlaubsantraege_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      alert('Export erfolgreich!');
    } catch (error) {
      console.error('Fehler beim Export:', error);
      alert('Fehler beim Export');
    }
  };

  const handleExportPDF = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API}/vacations/export/pdf`, {
        params: { year: selectedYear },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `urlaubsuebersicht_${selectedYear}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      alert('PDF erfolgreich exportiert!');
    } catch (error) {
      console.error('Fehler beim PDF-Export:', error);
      alert('Fehler beim PDF-Export');
    }
  };


  const handleImport = async () => {
    if (!importFile) {
      alert('Bitte wählen Sie eine Datei aus');
      return;
    }
    
    setImporting(true);
    try {
      const token = localStorage.getItem('authToken');
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await axios.post(
        `${API}/vacations/import/excel`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      alert(`Import erfolgreich!\nNeu importiert: ${response.data.imported}${response.data.errors.length > 0 ? '\n\nFehler:\n' + response.data.errors.join('\n') : ''}`);
      setShowImportDialog(false);
      setImportFile(null);
      fetchVacationOverview();
    } catch (error) {
      console.error('Fehler beim Import:', error);
      alert(error.response?.data?.detail || 'Fehler beim Import');
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Lade Urlaubsübersicht...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="p-6 max-w-md">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Fehler beim Laden</h3>
            <p className="text-slate-600 mb-4">{error}</p>
            <Button onClick={fetchVacationOverview} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Erneut versuchen
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header mit Jahr-Auswahl */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Admin Urlaubsübersicht</h2>
          <p className="text-slate-600">Übersicht aller Mitarbeiter-Urlaubsansprüche und -verbräuche</p>
        </div>
        
        <div className="flex items-center gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Jahr</label>
            <Select value={selectedYear.toString()} onValueChange={(value) => onYearChange(parseInt(value))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(year => (
                  <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExport} className="gap-2">
              <Download className="w-4 h-4" />
              Excel exportieren
            </Button>
            <Button variant="outline" onClick={handleExportPDF} className="gap-2">
              <Download className="w-4 h-4" />
              PDF exportieren
            </Button>
            <Button variant="outline" onClick={() => setShowImportDialog(true)} className="gap-2">
              <Upload className="w-4 h-4" />
              Excel importieren
            </Button>
          </div>
          
          <Button onClick={fetchVacationOverview} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Übersichts-Statistiken */}
      {overviewData?.overview_stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Mitarbeiter gesamt</p>
                  <p className="text-2xl font-bold text-slate-900">{overviewData.overview_stats.total_employees}</p>
                </div>
                <Users className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Überziehungen</p>
                  <p className="text-2xl font-bold text-red-600">{overviewData.overview_stats.overused_employees}</p>
                  <p className="text-xs text-slate-500">von {overviewData.overview_stats.total_employees}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Genommene Tage</p>
                  <p className="text-2xl font-bold text-green-600">{overviewData.overview_stats.total_used_days}</p>
                  <p className="text-xs text-slate-500">von {overviewData.overview_stats.total_annual_entitlement}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Durchschn. Auslastung</p>
                  <p className="text-2xl font-bold text-slate-900">{overviewData.overview_stats.average_utilization}%</p>
                </div>
                <TrendingUp className="w-8 h-8 text-slate-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Mitarbeiter-Übersicht */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TreePine className="w-5 h-5" />
            Mitarbeiter-Urlaubsübersicht {selectedYear}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {overviewData?.employees && overviewData.employees.length > 0 ? (
            <div className="space-y-3">
              {overviewData.employees.map((employee) => (
                <EmployeeVacationCard key={employee.employee_id} employee={employee} />
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <p className="text-slate-600">Keine Mitarbeiterdaten gefunden</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import Dialog */}
      {showImportDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Excel Import</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Excel-Datei auswählen</label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowImportDialog(false);
                    setImportFile(null);
                  }}
                >
                  Abbrechen
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={!importFile || importing}
                  className="gap-2"
                >
                  {importing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Importiere...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Importieren
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EmployeeVacationCard = ({ employee }) => {
  const utilizationColor = employee.utilization_percentage > 100 
    ? 'border-red-300 bg-red-50' 
    : employee.utilization_percentage > 80 
    ? 'border-orange-300 bg-orange-50'
    : 'border-slate-200 bg-white';

  return (
    <div className={`border rounded-lg p-4 transition-all ${utilizationColor}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <div>
              <h3 className="font-semibold text-slate-900">
                {employee.full_name}
              </h3>
              <p className="text-sm text-slate-600">
                {employee.employee_number} • {employee.role} • {employee.work_area}
              </p>
              <p className="text-xs text-slate-500">
                Eingestellt: {employee.hire_date ? new Date(employee.hire_date).toLocaleDateString('de-DE') : 'N/A'}
              </p>
            </div>
            
            {employee.is_overused && (
              <Badge className="bg-red-600 text-white gap-1">
                <AlertTriangle className="w-3 h-3" />
                Überziehung
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {/* Vertraglich festgelegter Jahresurlaub */}
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-medium text-blue-900">Jahresanspruch</span>
              </div>
              <p className="text-lg font-bold text-blue-600">{employee.annual_entitlement}</p>
              <p className="text-xs text-blue-700">Tage vertraglich</p>
            </div>

            {/* Bereits genommene Urlaubstage */}
            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-xs font-medium text-green-900">Genommen</span>
              </div>
              <p className="text-lg font-bold text-green-600">{employee.used_days}</p>
              <p className="text-xs text-green-700">Tage genehmigt</p>
            </div>

            {/* Beantragte Urlaubstage */}
            <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-yellow-600" />
                <span className="text-xs font-medium text-yellow-900">Beantragt</span>
              </div>
              <p className="text-lg font-bold text-yellow-600">{employee.pending_days}</p>
              <p className="text-xs text-yellow-700">Tage wartend</p>
            </div>

            {/* Verbleibende Urlaubstage */}
            <div className={`p-3 rounded-lg border ${
              employee.remaining_days < 0 
                ? 'bg-red-50 border-red-200' 
                : 'bg-emerald-50 border-emerald-200'
            }`}>
              <div className="flex items-center gap-2 mb-1">
                <TreePine className={`w-4 h-4 ${
                  employee.remaining_days < 0 ? 'text-red-600' : 'text-emerald-600'
                }`} />
                <span className={`text-xs font-medium ${
                  employee.remaining_days < 0 ? 'text-red-900' : 'text-emerald-900'
                }`}>Verbleibend</span>
              </div>
              <p className={`text-lg font-bold ${
                employee.remaining_days < 0 ? 'text-red-600' : 'text-emerald-600'
              }`}>
                {employee.remaining_days}
              </p>
              <p className={`text-xs ${
                employee.remaining_days < 0 ? 'text-red-700' : 'text-emerald-700'
              }`}>
                {employee.remaining_days < 0 ? 'Überzogen!' : 'Tage verfügbar'}
              </p>
            </div>

            {/* Auslastung */}
            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-slate-600" />
                <span className="text-xs font-medium text-slate-900">Auslastung</span>
              </div>
              <p className={`text-lg font-bold ${
                employee.utilization_percentage > 100 ? 'text-red-600' : 'text-slate-900'
              }`}>
                {employee.utilization_percentage}%
              </p>
              <p className="text-xs text-slate-700">
                {employee.total_requested} von {employee.annual_entitlement}
              </p>
            </div>

            {/* Anträge */}
            <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-purple-600" />
                <span className="text-xs font-medium text-purple-900">Anträge</span>
              </div>
              <p className="text-lg font-bold text-purple-600">{employee.total_requests}</p>
              <p className="text-xs text-purple-700">
                {employee.approved_requests} genehmigt
              </p>
            </div>
          </div>

          {/* Warnung bei Überziehung */}
          {employee.is_overused && (
            <div className="mt-3 p-3 bg-red-100 border border-red-300 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <div>
                  <p className="font-medium text-red-900">
                    Urlaubsanspruch überschritten!
                  </p>
                  <p className="text-sm text-red-700">
                    {employee.overuse_days} Tage über dem Jahresanspruch von {employee.annual_entitlement} Tagen.
                    Gesamtanfrage: {employee.total_requested} Tage.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminVacationOverview;